import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useOrg } from '../app/OrgProvider';
import { useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { IntervalToggle } from '../components/PlanCards';
import { UpgradePitch } from '../components/UpgradePitch';
import { TeamRoster } from '../components/TeamRoster';
import { CardIcon, XIcon } from '../components/icons';
import { Badge, Banner, Button, ErrorState, ErrorText, Loading, PageHeader } from '../components/ui';
import { trackBeginCheckout, trackPurchase, type SeatOrder } from '../lib/analytics';
import { ApiError, errorMessage } from '../lib/api';
import { dollars, shortDate } from '../lib/format';
import type { BillingInterval, OrgSummary, SubscriptionStatus } from '../lib/types';
import { qk } from '../queries/keys';
import {
  billingPortal,
  fetchOrg,
  previewInterval,
  seatPriceLabel,
  setBillingInterval,
  startCheckout,
  subscriptionNotice,
  yearlySavingsPercent,
} from '../queries/orgs';
import { SettingsTabs } from './SettingsPage';

/**
 * The subscription tab. One place for the plan: what you are on, what it
 * costs, who the seats are, and every way to change it. Stripe still holds
 * cards and invoices; everything else is here.
 */

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  incomplete: 'No plan yet',
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
};

const STATUS_TONE: Record<SubscriptionStatus, 'info' | 'go' | 'caution' | 'danger'> = {
  incomplete: 'caution',
  trialing: 'info',
  active: 'go',
  past_due: 'danger',
  canceled: 'caution',
};

/** Read once, on mount: Stripe Checkout sends people back here with ?checkout=. */
function readCheckoutOutcome(): 'success' | 'canceled' | null {
  const value = new URLSearchParams(window.location.search).get('checkout');
  return value === 'success' || value === 'canceled' ? value : null;
}

/** Stripe fills {CHECKOUT_SESSION_ID} into the return URL; it names the purchase. */
function readCheckoutSession(): string | null {
  return new URLSearchParams(window.location.search).get('session_id');
}

/** What one seat costs at this interval, in cents, with the list price as the fallback. */
function perSeatCentsFor(pricing: Parameters<typeof seatPriceLabel>[0], interval: BillingInterval): number {
  if (pricing) {
    return interval === 'year' && pricing.seatYearlyCents !== null ? pricing.seatYearlyCents : pricing.seatMonthlyCents;
  }
  return interval === 'year' ? 9000 : 900;
}

/**
 * The revenue conversion, sent once per checkout session. sessionStorage
 * remembers which sessions have been counted, so a reload of the return
 * page, or the back button, does not report the purchase again.
 */
function reportPurchaseOnce(order: SeatOrder, sessionId: string): void {
  const key = `slate.purchase.${sessionId}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Then it is counted, possibly twice on a reload; better than not at all.
  }
  trackPurchase(order, sessionId);
}

/** The plan, in one row: what it is on the left, the one thing to do on the right. */
function PlanRow({
  title,
  summary,
  badge,
  action,
  children,
}: {
  title: string;
  summary: string;
  badge?: ReactNode;
  action: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-body font-medium text-chalk">
            {title}
            {badge}
          </p>
          <p className="mt-0.5 text-caption text-chalk-dim">{summary}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function FreePlan() {
  const { pricing, refetch } = useOrg();
  return <UpgradePitch pricing={pricing} onCreated={() => refetch()} />;
}

/**
 * Monthly to yearly or back, with Stripe's number first: what is due today
 * and what the plan costs per period from here.
 */
function IntervalDialog({ org, interval, onClose }: { org: OrgSummary; interval: BillingInterval; onClose: () => void }) {
  const client = useQueryClient();
  const { refetch } = useOrg();
  const quote = useQuery({ queryKey: ['interval-quote', org.id, interval], queryFn: () => previewInterval(org.id, interval) });
  const change = useMutation({
    mutationFn: () => setBillingInterval(org.id, interval),
    onSuccess: () => {
      refetch();
      void client.invalidateQueries({ queryKey: qk.orgs() });
      onClose();
    },
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const q = quote.data;
  const label = interval === 'year' ? 'yearly' : 'monthly';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="interval-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-sm rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk">
          <XIcon size={16} />
        </button>
        <h2 id="interval-title" className="text-h4 text-chalk">
          Switch to {label} billing
        </h2>
        {quote.isPending ? <p className="mt-3 text-body text-chalk-dim">Getting the number from Stripe.</p> : null}
        {quote.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(quote.error)}</ErrorText>
          </div>
        ) : null}
        {q ? (
          <dl className="mt-4 divide-y divide-hairline rounded border border-hairline">
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">Due today</dt>
              <dd className="text-body text-chalk tnum">
                {q.billed ? dollars(q.dueNowCents) : '$0'}
                {q.billed && q.dueNowCents > 0 ? (
                  <span className="block text-caption text-chalk-faint">the {label} charge, less what this period already covered</span>
                ) : null}
                {q.billed && q.dueNowCents === 0 && interval === 'month' ? (
                  <span className="block text-caption text-chalk-faint">the unused part of the year is credited</span>
                ) : null}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 px-3 py-2.5">
              <dt className="text-caption text-chalk-dim">{q.billed ? 'Then' : 'After the trial'}</dt>
              <dd className="text-body text-chalk tnum">
                {dollars(q.perPeriodCents)} a {interval}
                <span className="block text-caption text-chalk-faint">
                  {org.seatCount} paid {org.seatCount === 1 ? 'seat' : 'seats'}
                </span>
              </dd>
            </div>
          </dl>
        ) : null}
        {change.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(change.error)}</ErrorText>
          </div>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={change.isPending} disabled={!q} onClick={() => change.mutate()}>
            {q?.billed && q.dueNowCents > 0 ? `Pay ${dollars(q.dueNowCents)} and switch` : `Switch to ${label}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** A team you belong to but do not own: the plan is someone else's to run. */
function MemberOfTeam({ org }: { org: OrgSummary }) {
  return (
    <PlanRow
      title={org.name}
      summary="You are on this team's plan, which its owner manages. Everything the plan includes, you have."
      badge={<Badge tone={STATUS_TONE[org.subscriptionStatus]}>{STATUS_LABEL[org.subscriptionStatus]}</Badge>}
      action={
        <AppLink to="/">
          <Button>Open the library</Button>
        </AppLink>
      }
    />
  );
}

/**
 * Restarting a plan that ended.
 *
 * The Stripe portal is the right door while a subscription exists; with none
 * it has nothing to show. This is the purchase again: how you want to pay,
 * how many seats, what it comes to, then checkout. No free week, because the
 * team has already had one.
 */
function ResubscribeDialog({
  org,
  seatsInUse,
  onClose,
}: {
  org: OrgSummary;
  /** Seats already occupied; the plan cannot come back smaller than the team. */
  seatsInUse: number;
  onClose: () => void;
}) {
  const { pricing } = useOrg();
  const [interval, setIntervalChoice] = useState<BillingInterval>(org.billingInterval ?? 'month');
  const [seats, setSeats] = useState(Math.max(org.seatCount, seatsInUse, 1));

  const checkout = useMutation({
    mutationFn: () => {
      trackBeginCheckout({ interval, seats, perSeatCents: perSeatCentsFor(pricing, interval) });
      return startCheckout(org.id, interval, seats);
    },
    onSuccess: (response) => window.location.assign(response.url),
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const price = seatPriceLabel(pricing, interval);
  const perSeatCents = pricing
    ? interval === 'year' && pricing.seatYearlyCents !== null
      ? pricing.seatYearlyCents
      : pricing.seatMonthlyCents
    : interval === 'year'
      ? 9000
      : 900;
  const savings = yearlySavingsPercent(pricing);
  const floor = Math.max(seatsInUse, 1);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="resubscribe-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk">
          <XIcon size={16} />
        </button>

        <h2 id="resubscribe-title" className="text-h3 text-chalk">
          Start {org.name} again
        </h2>
        <p className="mt-1.5 text-body text-chalk-dim">
          Everyone who was on the team is still here, and so is everything they recorded. Paying
          again puts the shared library back.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="flex items-baseline gap-2">
              <span className="text-h1 text-chalk tnum">{price.price}</span>
              <span className="text-body text-chalk-dim">{price.cadence}</span>
            </p>
            {interval === 'month' && savings > 0 ? (
              <p className="mt-0.5 text-caption text-go">Pay yearly and save {savings}%.</p>
            ) : null}
          </div>
          <IntervalToggle pricing={pricing} value={interval} onChange={setIntervalChoice} />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 rounded border border-hairline bg-slate-950/60 px-4 py-3">
          <span className="text-caption text-chalk-dim">Paid seats</span>
          <span className="inline-flex items-center rounded border border-hairline">
            <button
              type="button"
              onClick={() => setSeats((n) => Math.max(floor, n - 1))}
              disabled={seats <= floor}
              aria-label="Fewer seats"
              className="h-8 w-8 text-chalk-dim hover:bg-slate-800 hover:text-chalk disabled:opacity-40"
            >
              −
            </button>
            <span className="w-8 text-center text-body text-chalk tnum">{seats}</span>
            <button
              type="button"
              onClick={() => setSeats((n) => Math.min(200, n + 1))}
              aria-label="More seats"
              className="h-8 w-8 text-chalk-dim hover:bg-slate-800 hover:text-chalk"
            >
              +
            </button>
          </span>
          <span className="ml-auto text-body text-chalk tnum">
            {dollars(perSeatCents * seats)}{' '}
            <span className="text-caption text-chalk-dim">{interval === 'year' ? 'a year' : 'a month'}</span>
          </span>
        </div>
        <p className="mt-2 text-caption text-chalk-faint">
          {seatsInUse > 0
            ? `${seatsInUse} ${seatsInUse === 1 ? 'seat is' : 'seats are'} already occupied, so the plan cannot come back smaller. Free seats cost nothing.`
            : 'Free seats cost nothing.'}
        </p>

        {checkout.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(checkout.error)}</ErrorText>
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={checkout.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={checkout.isPending} onClick={() => checkout.mutate()}>
            Continue to checkout
          </Button>
        </div>
        <p className="mt-3 text-center text-caption text-chalk-faint">
          Billed today, with no free week: this team has had one.
        </p>
      </div>
    </div>
  );
}

function OwnedTeam({ org }: { org: OrgSummary }) {
  const { user } = useAuth();
  const { pricing } = useOrg();
  const detail = useQuery({ queryKey: qk.org(org.id), queryFn: () => fetchOrg(org.id) });
  const notice = subscriptionNotice(org.subscriptionStatus, org.trialEndsAt);

  const portal = useMutation({
    mutationFn: () => billingPortal(org.id),
    onSuccess: (response) => window.location.assign(response.url),
  });
  const checkout = useMutation({
    mutationFn: () => {
      const chosen: BillingInterval = org.billingInterval ?? 'month';
      trackBeginCheckout({ interval: chosen, seats: Math.max(org.seatCount, 1), perSeatCents: perSeatCentsFor(pricing, chosen) });
      return startCheckout(org.id);
    },
    onSuccess: (response) => window.location.assign(response.url),
  });

  const [resubscribing, setResubscribing] = useState(false);
  const billingSetUp = org.billingSetUp === true;
  const needsCheckout = !billingSetUp || org.subscriptionStatus === 'canceled' || org.subscriptionStatus === 'incomplete';
  // A plan that ended is bought again, not managed: the portal has nothing to
  // show without a subscription, and the choices made on the way in (how you
  // pay, how many seats) are the purchase, so they are asked here first.
  const lapsed = org.subscriptionStatus === 'canceled';
  const seatsInUse = detail.data?.seatsUsed ?? detail.data?.memberships.filter((m) => m.seat === 'paid').length ?? 0;
  const interval: BillingInterval = org.billingInterval ?? 'month';
  const yearlyExists = pricing?.seatYearlyCents !== null && pricing?.seatYearlyCents !== undefined;
  const [switching, setSwitching] = useState<BillingInterval | null>(null);
  const failure = portal.error ?? checkout.error;
  const notConfigured = failure instanceof ApiError && (failure.status === 400 || failure.status === 501);

  const perSeat = pricing ? (interval === 'year' && pricing.seatYearlyCents !== null ? pricing.seatYearlyCents : pricing.seatMonthlyCents) : null;
  const monthly = perSeat !== null ? `${dollars(perSeat * org.seatCount)} a ${interval}` : null;
  const when = org.subscriptionStatus === 'incomplete'
    ? 'checkout not finished'
    : org.cancelAt
    ? `ends ${shortDate(org.cancelAt)}`
    : org.subscriptionStatus === 'trialing'
      ? org.trialEndsAt
        ? `trial ends ${shortDate(org.trialEndsAt)}`
        : 'on trial'
      : org.renewsAt
        ? `renews ${shortDate(org.renewsAt)}`
        : STATUS_LABEL[org.subscriptionStatus].toLowerCase();
  const summary = [`${org.seatCount} paid ${org.seatCount === 1 ? 'seat' : 'seats'}${org.seatsUsed !== undefined ? ` (${org.seatsUsed} used)` : ''}`, monthly, when]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-6">
      {notice ? (
        <Banner tone={notice.tone} title={notice.title}>
          {notice.body}
        </Banner>
      ) : null}

      <PlanRow
        title={`Team · ${org.name}`}
        summary={summary}
        badge={
          <Badge tone={org.cancelAt ? 'caution' : STATUS_TONE[org.subscriptionStatus]}>
            {org.cancelAt ? 'Ending' : STATUS_LABEL[org.subscriptionStatus]}
          </Badge>
        }
        action={
          // One button, whatever the state: Checkout when Stripe holds no
          // subscription yet or the last one ended, the portal otherwise.
          <Button
            variant="primary"
            busy={checkout.isPending || portal.isPending}
            onClick={() => {
              if (lapsed) setResubscribing(true);
              else if (needsCheckout) checkout.mutate();
              else portal.mutate();
            }}
          >
            <CardIcon size={15} />
            {lapsed
              ? 'Restart subscription'
              : org.subscriptionStatus === 'incomplete'
                ? 'Finish checkout'
                : 'Manage subscription'}
          </Button>
        }
      >
        {yearlyExists && !lapsed ? (
          <p className="mt-3 text-caption text-chalk-faint">
            Billed {interval === 'year' ? 'yearly' : 'monthly'}.{' '}
            <button type="button" onClick={() => setSwitching(interval === 'year' ? 'month' : 'year')} className="text-signal hover:underline">
              Switch to {interval === 'year' ? 'monthly' : 'yearly'}
              {interval === 'month' && yearlySavingsPercent(pricing) > 0 ? ` and save ${yearlySavingsPercent(pricing)}%` : ''}
            </button>
          </p>
        ) : null}
        {switching ? <IntervalDialog org={org} interval={switching} onClose={() => setSwitching(null)} /> : null}
        {resubscribing ? (
          <ResubscribeDialog org={org} seatsInUse={seatsInUse} onClose={() => setResubscribing(false)} />
        ) : null}
        {failure ? (
          <div className="mt-3">
            {notConfigured ? (
              <Banner tone="caution" title="Billing is not configured on this deployment">
                The team still works on its trial and seats are tracked by the API; there is just no
                Stripe to open.
              </Banner>
            ) : (
              <ErrorText>{errorMessage(failure)}</ErrorText>
            )}
          </div>
        ) : null}
      </PlanRow>

      {detail.isPending ? <Loading label="Loading the team" /> : null}
      {detail.isError ? (
        <ErrorState
          title="Could not load the team"
          message={errorMessage(detail.error)}
          onRetry={() => void detail.refetch()}
        />
      ) : null}
      {detail.data ? (
        <TeamRoster
          detail={detail.data}
          viewerEmail={user?.email?.toLowerCase() ?? null}
          isOwner
          seatPrice={pricing ? dollars(pricing.seatMonthlyCents) : null}
          lapsed={org.subscriptionStatus === 'canceled'}
        />
      ) : null}
    </div>
  );
}

export function SubscriptionPage() {
  const { org, loading, error, refetch, pricing } = useOrg();
  const [outcome] = useState(readCheckoutOutcome);
  const [checkoutSession] = useState(readCheckoutSession);
  const orgs = org ? [org] : [];

  // Back from a completed checkout: report the purchase, once the team is
  // loaded so the seats and interval are the ones actually bought.
  useEffect(() => {
    if (outcome !== 'success' || !org) return;
    const interval: BillingInterval = org.billingInterval ?? 'month';
    reportPurchaseOnce(
      { interval, seats: Math.max(org.seatCount, 1), perSeatCents: perSeatCentsFor(pricing, interval) },
      checkoutSession ?? `${org.id}:${org.renewsAt ?? 'first'}`,
    );
  }, [checkoutSession, org, outcome, pricing]);

  const owned = orgs.filter((org) => org.role === 'owner');
  const memberOf = orgs.filter((org) => org.role !== 'owner');

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
      <SettingsTabs active="subscription" />

      {outcome === 'success' ? (
        <div className="mb-5">
          <Banner tone="go" title="Payment method saved">
            Stripe confirmed the checkout. If the status still says trial, give it a few seconds
            and reload; the webhook lands a moment after you do.
          </Banner>
        </div>
      ) : null}
      {outcome === 'canceled' ? (
        <div className="mb-5">
          <Banner tone="caution" title="Checkout canceled">
            Nothing was charged and nothing changed. Start checkout again whenever you like.
          </Banner>
        </div>
      ) : null}

      {loading ? <Loading label="Loading your plan" /> : null}
      {error ? (
        <ErrorState title="Could not load your plan" message={errorMessage(error)} onRetry={refetch} />
      ) : null}

      {!loading && !error ? (
        <div className="space-y-8">
          {owned.length === 0 ? <FreePlan /> : owned.map((org) => <OwnedTeam key={org.id} org={org} />)}
          {memberOf.length > 0 ? (
            <div className="space-y-4">
              {owned.length > 0 ? <h2 className="text-h4 text-chalk">Other teams</h2> : null}
              {memberOf.map((org) => (
                <MemberOfTeam key={org.id} org={org} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
